/* Copyright (c) 2020-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { When } from "react-if";
import { Box, theme, FlexContainer, LoadMore } from "@nordcloud/gnui";
import { Resource, useGetResourcesByTagsLazyQuery } from "~/generated/graphql";
import { useComponentsSelector } from "~/hooks";
import { useSearchState } from "~/hooks/useSearchState";
import { isNotNil } from "~/tools";
import { Item, SelectionMethod } from "~/types";
import { ResourceSidebar } from "~/views/resourceGroups/components/Resource/components/ResourceSidebar";
import { ResourceGroupExpression } from "~/views/resourceGroups/components/ResourceGroupExpression";
import {
  mapResourcesToItems,
  mapResourceToSelectedItem,
} from "~/views/resourceGroups/utils";
import { ResourceGroupData } from "../../ResourceGroupCreate/components/ResourceGroupCreateWizard/constants";
import { ResourceDragDrop } from "./components/ResourceDragDrop";

type Props = {
  resourceGroupData: ResourceGroupData;
  onSubmit: () => void;
  onDynamicSubmit: SubmitHandler<{ expression: string }>;
  selectedResources: string[];
  setSelectedResources: Dispatch<SetStateAction<string[]>>;
  allResources: Resource[];
  setAllResources: Dispatch<SetStateAction<Resource[]>>;
  dynamicResources: Item[];
  setDynamicResources: Dispatch<SetStateAction<Item[]>>;
  handleReorder: (ids: string[]) => void;
  setExpression: (expression?: string) => void;
};

export function ResourceSelect({
  resourceGroupData,
  onSubmit,
  onDynamicSubmit,
  selectedResources,
  setSelectedResources,
  allResources,
  setAllResources,
  dynamicResources,
  setDynamicResources,
  handleReorder,
  setExpression,
}: Props) {
  const [selectedOptions, setSelectedOptions] = useState(selectedResources);
  const { searchState, updateSearchState } = useSearchState({
    page: 1,
    limit: 100,
  });
  const [totalDynamicCount, setTotalDynamicCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [getResourcesByTags] = useGetResourcesByTagsLazyQuery({
    fetchPolicy: "cache-and-network",
  });

  const manualOptions = useMemo(() => {
    return selectedOptions
      .map((id) => allResources.find((option) => option.id === id))
      .filter(isNotNil)
      .map(mapResourceToSelectedItem);
  }, [allResources, selectedOptions]);

  const { items, handleShowSearch, handleDelete, handleDragEnd } =
    useComponentsSelector([...manualOptions, ...dynamicResources]);

  const handleRemoveItem = (item: Item) => {
    setSelectedOptions((selectedItems) =>
      selectedItems.filter((option) => option !== item.id)
    );
    setSelectedResources((selectedItems) =>
      selectedItems.filter((option) => option !== item.id)
    );
    handleDelete(item);
  };

  const handleManualSubmit = (ids: string[]) => {
    setSelectedOptions(ids);
    setSelectedResources(ids);
    onSubmit();
    const manual = ids
      .map((id) => allResources.find((option) => option.id === id))
      .filter(isNotNil)
      .map(mapResourceToSelectedItem);
    handleShowSearch([...manual, ...dynamicResources]);
  };

  const handleDynamicUpdate = (dynamicItems: Item[]) => {
    setDynamicResources(dynamicItems);
    handleShowSearch([...manualOptions, ...dynamicItems]);
    updateSearchState({ page: 1 });
  };

  const handlePageInfoChange = (pageInfo?: {
    count?: number;
    pages?: number;
  }) => {
    setTotalDynamicCount(pageInfo?.count ?? 0);
  };

  const loadDynamicPage = async (
    page: number,
    mergeMode: "append" | "replace"
  ) => {
    if (!resourceGroupData.expression) {
      if (mergeMode === "replace") {
        updateSearchState({ page: 1 });
      }
      return;
    }

    setIsLoadingMore(true);

    const { data } = await getResourcesByTags({
      variables: {
        tagsExpression: resourceGroupData.expression,
        page,
        limit: searchState.limit,
      },
    });
    const pageItems: Item[] = mapResourcesToItems(
      data?.resourcesByTags?.result ?? [],
      SelectionMethod.dynamic
    ) as Item[];

    const nextItems =
      mergeMode === "append" ? [...dynamicResources, ...pageItems] : pageItems;

    setDynamicResources(nextItems);
    handleShowSearch([...manualOptions, ...nextItems]);
    setTotalDynamicCount(data?.resourcesByTags?.pageInfo?.count ?? 0);
    updateSearchState({ page });
    setIsLoadingMore(false);
  };

  useEffect(() => {
    const ids = items.SELECTED_ITEMS.filter(
      (item) => item.tag === SelectionMethod.manual
    ).map((item) => item.id);

    setSelectedResources(ids);
  }, [
    handleShowSearch,
    items.SELECTED_ITEMS,
    manualOptions,
    setSelectedResources,
  ]);

  return (
    <>
      <ResourceGroupExpression
        defaultValue={resourceGroupData.expression}
        handleDynamicUpdate={handleDynamicUpdate}
        setExpression={setExpression}
        onSubmit={onDynamicSubmit}
        onPageInfoChange={handlePageInfoChange}
        limit={searchState.limit}
        disableExpressionDeleteModal
      />
      <Box border="grey" padding={0}>
        <FlexContainer
          padding={`${theme.spacing.spacing03} ${theme.spacing.spacing04}`}
          css={{ borderBottom: theme.borders.grey }}
        >
          <ResourceSidebar
            handleManualSubmit={handleManualSubmit}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            setAllResources={setAllResources}
          />
        </FlexContainer>
      </Box>
      <Box
        css={{ minHeight: "25rem", borderRadius: 0 }}
        backgroundColor={theme.color.background.ui04}
      >
        <form id="wizard_step_2" onSubmit={onSubmit}>
          <ResourceDragDrop
            {...{
              items,
              handleDragEnd,
              handleRemoveItem,
              handleReorder,
            }}
          />
        </form>
        <When condition={items.SELECTED_ITEMS.length > 0}>
          <LoadMore
            currentCount={items.SELECTED_ITEMS.length}
            total={manualOptions.length + totalDynamicCount}
            onLoadMore={() => loadDynamicPage(searchState.page + 1, "append")}
            onLoadLess={
              items.SELECTED_ITEMS.length > searchState.limit
                ? () => loadDynamicPage(1, "replace")
                : undefined
            }
            isLoading={isLoadingMore}
            css={{ marginTop: theme.spacing.spacing04 }}
          />
        </When>
      </Box>
    </>
  );
}
